import React, {
    useEffect,
    useState,
    useRef,
    useCallback,
    MutableRefObject,
} from 'react';
import cn from 'classnames';
import { Element } from 'src/components';
import DropdownButton from './DropdownButton';
import DropdownContent from './DropdownContent';

interface IProps {
    name: string;
}

export interface IDropdown {
    children?: any;
    triggerBefore?: any;
    className?: string;
}

export function useDropdown({ name }: IProps) {
    const [isExpand, setExpand] = useState(false);
    const [buttonName, setName] = useState(name);
    const contentRef: MutableRefObject<HTMLElement | any> = useRef({});
    const btnRef: MutableRefObject<HTMLElement | any> = useRef({});

    const closeDropdown = () => {
        setExpand(false);
    };

    const openDropdown = () => {
        setExpand(true);
    };

    const toggleDropdown = useCallback(() => {
        setExpand(!isExpand);
    }, [isExpand]);

    const onMouseUp = useCallback(
        (e: MouseEvent) => {
            if (btnRef.current.contains(e.target)) {
                toggleDropdown();
            } else if (!contentRef.current.contains(e.target)) {
                closeDropdown();
            }
        },
        [contentRef, btnRef, isExpand]
    );

    useEffect(() => {
        window.addEventListener('mouseup', onMouseUp);
        return () => {
            window.removeEventListener('mouseup', onMouseUp);
        };
    }, [isExpand]);

    const Dropdown = useCallback(
        (props: IDropdown) => {
            const { className, triggerBefore = null } = props;
            return (
                <Element
                    className={cn('bm-dropdown', 'relative', className)}
                >
                    <Element
                        className={cn(
                            'bm-dropdown-trigger',
                            'flex items-center justify-between',
                            'px-[15px] py-[10px]',
                            'text-[1.2rem] text-[var(--text-item-hover)] leading-[1]',
                            'min-w-[120px]',
                            'rounded-full',
                            'bg-[var(--alpha-bg)]'
                        )}
                    >
                        {triggerBefore}
                        <DropdownButton
                            name={buttonName}
                            isExpand={isExpand}
                            domRef={btnRef}
                        />
                    </Element>
                    <DropdownContent domRef={contentRef} isShow={isExpand}>
                        {props.children}
                    </DropdownContent>
                </Element>
            );
        },
        [isExpand, name, buttonName]
    );
    return { isExpand, openDropdown, closeDropdown, Dropdown, setName };
}

export default useDropdown;
